import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Box, Link, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import request from 'api'
import { Loading } from 'components/ui'
import { useDateTime, useMappedTranslation } from 'hooks'
import { getConsentApi } from 'pages/policies/api'
import { Accordion, Action, Consent, appendLegacyParams, buildPolicySearch, getListLength, trim } from 'pages/policies/utils'
import Detail from 'pages/policies/views/detail'
import i18n from 'utils/i18n'

import PrivacyIcon from '../../assets/images/privacy_icon.png'

enum PRIVACY_TYPE {
  MAJOR = 'PDP',
  MINOR = 'MPC'
}

export default function KorPrivacyNoticesLatest() {
  const t = useMappedTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const dateTime = useDateTime()
  const type = searchParams.get('type') === 'minor' ? PRIVACY_TYPE.MINOR : PRIVACY_TYPE.MAJOR
  const paramCountryCode = searchParams.get('countryCode') || undefined
  const paramLocale = searchParams.get('locale') || undefined
  const policySearch = buildPolicySearch(searchParams)
  const minorSearch = buildPolicySearch(searchParams, { type: 'minor' })

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [consent, setConsent] = useState<Consent>()
  const [detail, setDetail] = useState<string>('<div/>')
  const [title, setTitle] = useState<string>('')
  const [accordions, setAccordions] = useState<Accordion[]>([])
  const [action, setAction] = useState<Action>({} as Action)

  const getDetailApi = (uri: string) => {
    const LEGACY_HOST = 'https://account.samsung.com'
    return request
      .get(uri)
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentBody')
        }

        const document = new DOMParser().parseFromString(res.data, 'text/html')
        const _title = document.getElementById('ppTitle')?.innerText || ''

        const language = paramLocale?.replace('-', '_') || i18n.language?.replace('-', '_')
        if (language) {
          document.querySelectorAll('a').forEach((tag) => {
            if (tag.href.includes(`${LEGACY_HOST}`)) {
              tag.href = appendLegacyParams(tag.href, language, paramCountryCode)
            }
          })
        }

        let _detail = trim(document.getElementById('ppContent')?.innerHTML || '')
        const accordionContainer: Accordion[] = []
        const contentLength = getListLength(document)
        for (let i = 1; i <= contentLength; i++) {
          let title = document.getElementById(`listTitle${String(i).padStart(2, '0')}`)?.innerHTML || ''
          let description = trim(document.getElementById(`listContent${String(i).padStart(2, '0')}`)?.innerHTML) || ''
          accordionContainer.push({ title, description })
        }

        setTitle(_title)
        setDetail(_detail)
        setAccordions(accordionContainer)
        setAction({ uri })
      })
      .catch(() => setDetail('<div>Failed to load</div>'))
  }

  const renderActions = () => {
    switch (type) {
      case PRIVACY_TYPE.MAJOR:
        return (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                pt: { xs: '52px', md: '72px' }
              }}
            >
              <Typography
                className='off'
                fontSize='13px'
                fontWeight='700'
                color={theme.palette.mode === 'dark' ? '#A9A9A9' : '#636363'}
              >
                {t('iam.01.Common.Txt.Effective.date', { 0: dateTime(Number(consent?.publish), { format: 'noticeDate' }) })}
              </Typography>
              <Box sx={{ marginTop: '24px', height: '24px' }}>
                <Link
                  onClick={() => window.open(consent?.uri || '', '_blank')}
                  component='button'
                  type='button'
                  fontSize='16px'
                  fontWeight='700'
                  lineHeight='24px'
                  color={theme.palette.mode === 'dark' ? '#A9A9A9' : '#636363'}
                  sx={{ width: '100%', textDecoration: 'underline' }}
                  data-testid='LinkBACAB802'
                >
                  {t('iam.01.Common.Text.Trouble.reading.our.Privacy.Notice')}
                </Link>
              </Box>
            </Box>
          </>
        )
      case PRIVACY_TYPE.MINOR:
        return (
          <Box sx={{ marginTop: '24px' }}>
            <Typography
              tabIndex={0}
              onClick={() => window.open(action.uri, '_blank')}
              color={theme.palette.mode === 'dark' ? '#A9A9A9' : '#505050'}
              sx={{
                cursor: 'pointer',
                width: '100%',
                textDecoration: 'underline',
                fontSize: '15px',
                fontWeight: 700,
                lineHeight: '24px'
              }}
              data-testid='Typography2BE8715C'
            >
              {t('iam.01.Common.Text.Trouble.reading.our.Privacy.Notice')}
            </Typography>
          </Box>
        )
    }
  }

  useEffect(() => {
    setIsLoading(true)
    getConsentApi(type.toString(), false)
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentApi')
        } else {
          const data = res.data[0] as Consent
          setConsent(data)
          return data
        }
      })
      .then((data) => {
        return getDetailApi(data.uri)
      })
      .catch(() => {
        setConsent(undefined)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [type, paramCountryCode, paramLocale])

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {isLoading ? (
        <Loading fullScreen />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: { xs: '0 24px', lg: '0' } }}>
          <Box sx={{ width: { xs: '100%', lg: '1040px' } }}>
            <Detail
              title={title}
              titleIcon={PrivacyIcon}
              detail={detail}
              accordions={accordions}
              isLoading={false}
              action={renderActions()}
              minorButton={
                type === PRIVACY_TYPE.MAJOR && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '16px'
                    }}
                  >
                    <Link
                      component='button'
                      type='button'
                      sx={{
                        textDecoration: 'none',
                        fontSize: '15px',
                        lineHeight: '24px',
                        fontWeight: '700',
                        color: '#0381FE'
                      }}
                      onClick={() => {
                        setIsLoading(true)
                        navigate({
                          pathname: '/policies/privacy-notices/latest',
                          search: minorSearch
                        })
                      }}
                      data-testid='Link2BFC393E'
                    >
                      {t('iam.01.Common.button.Easy.to.understand.Privacy.Notice')}
                    </Link>

                    <Link
                      component='button'
                      type='button'
                      sx={{
                        textDecoration: 'none',
                        fontSize: '15px',
                        lineHeight: '24px',
                        fontWeight: '700',
                        color: '#0381FE'
                      }}
                      onClick={() => {
                        setIsLoading(true)
                        navigate({
                          pathname: '/policies/privacy-notices',
                          search: policySearch
                        })
                      }}
                      data-testid='LinkC3D6292A'
                    >
                      {t('iam.01.Common.Notice.detail.list.01')}
                    </Link>
                  </Box>
                )
              }
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}
